import { ViewController } from '@estee/elc-service';
import { action, observable, computed } from 'mobx';
import { Config } from '~domain/entities/Config';
import { StoresMap } from '~domain/entities/StoresMap';
import { UserLocationRepository, INavigatorPosition } from '~repositories/UserLocationRepository';
import { IStoresMapConfig, IAddressSearch } from '~interfaces/IStoresInterfaces';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/StoresService';
import { IGeoLocation } from '~domain/entities/UserLocation';
import {
    IAddressSelectedStrategy,
    IMapPanPosition,
    StoreLocatorAddressSelectedStrategy
} from '../strategies/StoresLandingAddressSelectedStrategies';
import { UrlFormatter } from '@estee/elc-formatters';
import { FiltersRepository } from '~repositories/FiltersRepository';
import { StoresRepository } from '~repositories/StoresRepository';
import { BookVirtualAppointmentClickedUseCase } from '~use-cases/BookVirtualAppointmentClickedUseCase';
import { ToggleErrorMessageCloseUseCase } from '~use-cases/ToggleErrorMessageUseCase';
import { ToggleFilterModalUseCase } from '~use-cases/ToggleFilterModalUseCase';
import { GoToDirectoryClickedUseCase } from '~use-cases/GoToDirectoryClickedUseCase';
import { events, emit } from '@estee/elc-service-bus';

export class StoresViewController extends ViewController {
    @observable private storesMap: StoresMap;
    @observable public configService: Config;
    @observable public userLocationRepository: UserLocationRepository;
    @observable public filtersRepository: FiltersRepository;
    @observable public storesRepository: StoresRepository;
    @observable public counterId: string;
    @observable public isSelecting: boolean;
    private fetchDataStrategy: IAddressSelectedStrategy;
    private urlFormatter: UrlFormatter;

    constructor(
        storesMap: StoresMap,
        fetchDataStrategy: IAddressSelectedStrategy = new StoreLocatorAddressSelectedStrategy()
    ) {
        super();
        this.storesMap = storesMap;
        this.configService = diContainer.get(serviceNames.config);
        this.userLocationRepository = diContainer.get(serviceNames.userLocationRepository);
        this.storesRepository = diContainer.get(serviceNames.storesRepository);
        this.fetchDataStrategy = fetchDataStrategy;
        this.urlFormatter = diContainer.get(serviceNames.urlFormatter);
        this.filtersRepository = diContainer.get(serviceNames.filtersRepository);
    }

    @action
    public loaded = async (mapConfig: IStoresMapConfig) => {
        try {
            const { storesDefaultSearchLocation, oabSelectedStoreId } = this.configService.config;
            await this.userLocationRepository.getUserLocation();
            const { geolocation: userLocation } = this.userLocationRepository.userLocation;
            const { mapBounds, mapCenter, mapZoom } = mapConfig;
            const userSearchedLocation = this.userLocationRepository.getUserSearchedLocation();
            if (oabSelectedStoreId) {
                const store = this.storesRepository.getStoreFromCounterId(
                    String(oabSelectedStoreId)
                );
                if (store) {
                    this.updateSelectedStore(String(oabSelectedStoreId), true);
                    const coordinates: IGeoLocation = {
                        lat: store.geographical.coordinates.lat,
                        lng: store.geographical.coordinates.lon
                    };
                    await this.updateMapLocation(coordinates);
                }
            } else if (userSearchedLocation) {
                await this.fetchDataStrategy.onAddressSelected(userSearchedLocation);
            } else if (userLocation) {
                await this.updateMapLocation(userLocation);
            } else if (storesDefaultSearchLocation) {
                await this.updateMapLocation(storesDefaultSearchLocation);
            } else {
                mapBounds && this.storesMap.convertBounds(mapBounds);
                mapCenter && this.storesMap.updateMapCenter(mapCenter);
                mapZoom && this.storesMap.updateMapZoom(mapZoom);
            }
        } finally {
            this.isLoading = false;
        }
    };

    @action
    public onVirtualOnlineAppoinmentClick = () => {
        new BookVirtualAppointmentClickedUseCase().execute();
    };

    public onAddressSelected = async (searchAddress: IAddressSearch) => {
        this.userLocationRepository.saveUserSearchedLocation(searchAddress);
        await this.fetchDataStrategy.onAddressSelected(searchAddress);
        emit(events.STORE_SEARCH_ADDRESS_SUBMITTED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_search',
            eventAction: 'search',
            eventLabel: `${this.storesRepository.stores.length}`
        });
    };

    public onMapPan = async (center: IMapPanPosition) => {
        await this.fetchDataStrategy.onMapPan(center);
    };

    public onLocationReset = async (position: INavigatorPosition) => {
        emit(events.STORE_LOCATION_RESET_CLICKED, {
            eventName: 'store_finder',
            eventCategory: 'store_location_reset_clicks',
            eventAction: 'click',
            eventLabel: 'Geolocation'
        });
        await this.userLocationRepository.resetUserLocation(position);
        await this.fetchDataStrategy.onLocationReset();
    };

    public onTabSelection = async (tabName: string) => {
        emit(events.STORE_TAB_VIEW_SECTION_CLICKED, {
            eventName: 'store_finder',
            eventCategory: 'store_tab_view_section_clicks',
            eventAction: 'click',
            eventLabel: `${tabName}`
        });
    };

    public getGeolocationStatus = async (): Promise<PermissionState> => {
        const status = await this.userLocationRepository.fetchGeolocationPermissions();

        return status;
    };

    @action
    public updateSelectedStore = (counterId: string = '', isSelecting: boolean = false) => {
        this.counterId = counterId;
        this.isSelecting = isSelecting;
    };

    @action
    public onErrorMessageCloseClick = () => new ToggleErrorMessageCloseUseCase(false).execute();

    @action
    public onFilterModalCloseClick = () => new ToggleFilterModalUseCase(false).execute();

    @action
    public onFilterButtonClick = () => new ToggleFilterModalUseCase(true).execute();

    @action
    public onGoToDirectoryClick = () => {
        emit(events.STORE_DIRECTORY_LINK_CLICKED, {
            eventName: 'store_directory',
            eventCategory: 'store_finder_directory_click',
            eventAction: 'store_directory',
            eventLabel: 'store_directory'
        });
        new GoToDirectoryClickedUseCase().execute();
    };

    @action
    public storeHoursToggled = async (isExpanded: boolean, name: string) => {
        emit(events.STORE_OPENING_HOURS_TOGGLED, {
            eventName: 'store_finder',
            eventCategory: 'store_finder_map_selection',
            eventAction: isExpanded ? 'expand' : 'collapse',
            eventLabel: name
        });
    };

    @computed
    private get breadcrumbsUrls() {
        return {
            homeUrl: this.urlFormatter.langPrefixUrl('/')
        };
    }

    @computed
    private get numberOfActiveFilters() {
        const { booleanFilters, multiOptionFilters, servicesAvailableFilter, inStorePickupFilter } =
            this.filtersRepository;
        const activeBooleanFiltersCount = Object.values(booleanFilters).filter(Boolean).length;

        const activeMultiOptionFiltersCount = Object.values(multiOptionFilters).reduce(
            (counter, filters) => counter + filters.length,
            0
        );

        return (
            activeBooleanFiltersCount +
            activeMultiOptionFiltersCount +
            Number(servicesAvailableFilter) +
            Number(inStorePickupFilter)
        );
    }

    @computed
    get data() {
        return {
            config: this.configService.config,
            breadcrumbsUrls: this.breadcrumbsUrls,
            isErrorMessageOpen: this.storesRepository.isErrorMessageOpen,
            isFilterModalOpen: this.storesRepository.isFilterModalOpen,
            areStoresLoading: this.storesRepository.isLoading,
            stores: this.storesRepository.stores,
            selectedStoreId: this.storesRepository.selectedStoreId,
            geolocationPermissions: this.getGeolocationStatus,
            numberOfActiveFilters: this.numberOfActiveFilters
        };
    }

    private async updateMapLocation(geolocation: IGeoLocation) {
        this.storesMap.updateMapCenter({
            lat: String(geolocation.lat),
            lng: String(geolocation.lng)
        });

        return this.fetchDataStrategy.onUpdateMapLocation(geolocation);
    }
}
